import React, { useMemo, useCallback } from 'react';
import type { FC } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, xcss } from '@atlaskit/primitives';

import { CommentNavigation as CommentNavigationCommon } from '@confluence/inline-comments-common/entry-points/CommentNavigation';
import type { Comment } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { fg } from '@confluence/feature-gating';
import {
	useAnnotationsDispatch,
	useAnnotations,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';

import { useInlineCommentNavigation } from './hooks/useInlineCommentNavigation';

type CommentNavigationProps = {
	pageId: string;
	commentData: Comment;
	isEditor?: boolean;
	onNavigationClick: (nextMarkerRef: string) => void;
	disableCommentCount: boolean;
};

const positionRightStyles = xcss({
	position: 'absolute',
	top: 'space.150',
	right: 'space.800',
});

const positionLeftStyles = xcss({
	position: 'absolute',
	top: 'space.150',
	left: 'space.150',
});

export const CommentNavigation: FC<CommentNavigationProps> = ({
	pageId,
	commentData,
	isEditor,
	onNavigationClick,
	disableCommentCount,
}) => {
	const { clearStoredCommentData } = useAnnotationsDispatch();
	const { storedCommentData } = useAnnotations();

	const {
		currentCommentIndex,
		goToNextInlineComment: originalGoToNext,
		goToPrevInlineComment: originalGoToPrev,
		commentsCount,
	} = useInlineCommentNavigation({
		isEditor,
		pageId,
		onNavigationClick,
		commentData,
	});

	// Wrap navigation functions to clear stored comment data before navigation
	const goToNextInlineComment = useCallback(() => {
		if (clearStoredCommentData && storedCommentData) {
			clearStoredCommentData();
		}
		originalGoToNext();
	}, [clearStoredCommentData, storedCommentData, originalGoToNext]);

	const goToPrevInlineComment = useCallback(() => {
		if (clearStoredCommentData && storedCommentData) {
			clearStoredCommentData();
		}
		originalGoToPrev();
	}, [clearStoredCommentData, storedCommentData, originalGoToPrev]);

	return useMemo(
		() => (
			<Inline
				xcss={[
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					fg('confluence-frontend-comments-panel') &&
						!fg('confluence_frontend_content_wrapper') &&
						positionRightStyles,
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					(fg('confluence_frontend_content_wrapper') ||
						!fg('confluence-frontend-comments-panel')) &&
						positionLeftStyles,
				]}
			>
				<CommentNavigationCommon
					goToNextInlineComment={goToNextInlineComment}
					goToPrevInlineComment={goToPrevInlineComment}
					currentCommentIndex={currentCommentIndex}
					commentsCount={commentsCount}
					disableCommentCount={disableCommentCount}
				/>
			</Inline>
		),
		[
			commentsCount,
			currentCommentIndex,
			goToNextInlineComment,
			goToPrevInlineComment,
			disableCommentCount,
		],
	);
};
